import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Descriptions, Modal, Select, Tag } from "antd";
import {
  getPatientConsent,
  updatePatientConsent,
  updatePatientConsentStatus,
  openPatientConsentPdf,
} from "../../apis/consent";
import { PatientConsent } from "../../interfaces/Consent";
import { useNotification } from "../../NotificationContext";

const STATUS_COLORS: Record<string, string> = {
  generated: "default",
  printed: "blue",
  signed_uploaded: "green",
  void: "red",
};

const STATUS_TRANSITIONS: Record<string, string[]> = {
  generated: ["printed", "void"],
  printed: ["signed_uploaded", "void"],
  signed_uploaded: ["void"],
  void: [],
};

const inputCls =
  "block w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm text-gray-900 focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-400";

const PatientConsentDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const [consent, setConsent] = useState<PatientConsent | null>(null);
  const [loading, setLoading]     = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);

  // Status modal
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [updating, setUpdating]   = useState(false);

  // Edit mode
  const [editing, setEditing]       = useState(false);
  const [editData, setEditData]     = useState<Record<string, string>>({});
  const [editNotes, setEditNotes]   = useState("");
  const [saving, setSaving]         = useState(false);

  const load = () => {
    setLoading(true);
    getPatientConsent(Number(id))
      .then((res: any) => setConsent(res.data ?? res))
      .catch(() => showNotification("error", "Failed to load consent", "", "topRight"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [id]);

  const startEditing = () => {
    if (!consent) return;
    setEditData({ ...(consent.filledData ?? {}) });
    setEditNotes(consent.notes ?? "");
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setEditData({});
    setEditNotes("");
  };

  const handleSave = async (andOpenPdf = false) => {
    setSaving(true);
    try {
      const res: any = await updatePatientConsent(Number(id), {
        filledData: editData,
        notes: editNotes || null,
      });
      const updated = res.data ?? res;
      setConsent(updated);
      setEditing(false);
      showNotification("success", "Consent updated", "", "topRight");

      if (andOpenPdf) {
        try {
          await openPatientConsentPdf(Number(id));
        } catch {
          showNotification("error", "Saved but PDF generation failed", "", "topRight");
        }
      }
    } catch {
      showNotification("error", "Failed to save changes", "", "topRight");
    } finally {
      setSaving(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!newStatus) return;
    setUpdating(true);
    try {
      const res: any = await updatePatientConsentStatus(Number(id), newStatus);
      setConsent(res.data ?? res);
      setStatusModalOpen(false);
      showNotification("success", "Status updated", "", "topRight");
    } catch {
      showNotification("error", "Failed to update status", "", "topRight");
    } finally {
      setUpdating(false);
    }
  };

  if (loading || !consent) return <div className="p-6 text-gray-400">Loading...</div>;

  const nextStatuses = STATUS_TRANSITIONS[consent.status] ?? [];

  // Build an ordered list of fields for display/edit
  // Priority: template fields order, then any extra keys in filledData not in the template
  const templateFields = consent.template?.fields ?? [];
  const templateKeys = new Set(templateFields.map((f) => f.fieldKey));
  const extraKeys = Object.keys(consent.filledData ?? {}).filter((k) => !templateKeys.has(k));

  const allFields: Array<{ key: string; label: string }> = [
    ...templateFields.map((f) => ({ key: f.fieldKey, label: f.label ?? f.fieldKey })),
    ...extraKeys.map((k) => ({ key: k, label: k })),
  ].filter(({ key }) => key !== "procedure_name"); // auto-filled server-side, no need to show

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold">Consent {consent.reference}</h1>
          <Tag color={STATUS_COLORS[consent.status]} className="mt-1">
            {consent.status.replace(/_/g, " ")}
          </Tag>
        </div>
        <div className="flex gap-2 flex-wrap">
          {!editing && consent.status === "generated" && (
            <Button onClick={startEditing}>Edit Values</Button>
          )}
          {!editing && nextStatuses.length > 0 && (
            <Button onClick={() => { setNewStatus(""); setStatusModalOpen(true); }}>
              Update Status
            </Button>
          )}
          {!editing && (
            <Button
              type="primary"
              loading={pdfLoading}
              onClick={async () => {
                setPdfLoading(true);
                try {
                  await openPatientConsentPdf(consent.id);
                } catch {
                  showNotification("error", "Failed to load PDF", "", "topRight");
                } finally {
                  setPdfLoading(false);
                }
              }}
            >
              View / Print PDF
            </Button>
          )}
          {!editing && <Button onClick={() => navigate(-1)}>Back</Button>}
        </div>
      </div>

      {/* Metadata */}
      <Descriptions bordered size="small" className="mb-6" column={2}>
        <Descriptions.Item label="Patient">
          {consent.patient
            ? `${consent.patient.firstName} ${consent.patient.lastName}`
            : `#${consent.patientId}`}
        </Descriptions.Item>
        <Descriptions.Item label="Visit">
          {consent.visitId ? `#${consent.visitId}` : "—"}
        </Descriptions.Item>
        <Descriptions.Item label="Template">
          {consent.template?.name ?? `#${consent.templateId}`}
        </Descriptions.Item>
        <Descriptions.Item label="Created">
          {new Date(consent.createdAt).toLocaleString()}
        </Descriptions.Item>
        {consent.notes && !editing && (
          <Descriptions.Item label="Notes" span={2}>
            {consent.notes}
          </Descriptions.Item>
        )}
      </Descriptions>

      {/* Edit form */}
      {editing ? (
        <div className="rounded-lg border bg-white p-5 mb-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Edit Filled Values</h3>
          <div className="space-y-4">
            {allFields.map(({ key, label }) => (
              <div key={key}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {label}
                </label>
                <input
                  type="text"
                  value={editData[key] ?? ""}
                  onChange={(e) => setEditData((prev) => ({ ...prev, [key]: e.target.value }))}
                  className={inputCls}
                />
              </div>
            ))}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <textarea
                rows={2}
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                className={inputCls}
              />
            </div>
          </div>

          <div className="flex gap-2 justify-end mt-5">
            <Button onClick={cancelEditing} disabled={saving}>
              Cancel
            </Button>
            <Button loading={saving} onClick={() => handleSave(false)}>
              Save
            </Button>
            <Button type="primary" loading={saving} onClick={() => handleSave(true)}>
              Save & View PDF
            </Button>
          </div>
        </div>
      ) : (
        /* Read-only filled data */
        allFields.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold mb-3">Filled Values</h3>
            <div className="rounded-lg border bg-white divide-y">
              {allFields.map(({ key, label }) => (
                <div key={key} className="flex gap-4 px-4 py-2.5 text-sm">
                  <span className="text-gray-500 w-48 shrink-0">{label}</span>
                  <span className="text-gray-900">{consent.filledData?.[key] ?? "—"}</span>
                </div>
              ))}
            </div>
          </div>
        )
      )}

      {/* Status modal */}
      <Modal
        title="Update Consent Status"
        open={statusModalOpen}
        onOk={handleStatusUpdate}
        confirmLoading={updating}
        onCancel={() => setStatusModalOpen(false)}
        okText="Update"
      >
        <p className="text-sm text-gray-500 mb-3">
          Current status: <strong>{consent.status.replace(/_/g, " ")}</strong>
        </p>
        <Select
          style={{ width: "100%" }}
          value={newStatus || undefined}
          onChange={setNewStatus}
          placeholder="Select new status..."
          options={nextStatuses.map((s) => ({
            label: s.replace(/_/g, " "),
            value: s,
          }))}
        />
      </Modal>
    </div>
  );
};

export default PatientConsentDetail;
